import { useEffect, useRef, useState } from 'react';
import { Button, makeStyles, Spinner, Text, Tooltip, tokens } from '@fluentui/react-components';
import { Add20Regular, Delete20Regular, PanelLeftContract20Regular, PanelLeftExpand20Regular } from '@fluentui/react-icons';
import { apiClient } from '../../../services/api/apiClient';
import { createSpeechPonyfillFactory } from '../../../utils/speechPonyfill';
import { createManualSubmitWebChatStore } from '../../../utils/webchatStore';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    padding: tokens.spacingHorizontalL,
  },
  header: {
    paddingBottom: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalM,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
  },
  body: {
    flex: 1,
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    minHeight: 0,
  },
  sidebar: {
    width: '260px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    borderRadius: '16px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    padding: tokens.spacingHorizontalM,
    overflowY: 'auto',
  },
  sidebarHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sessionItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  sessionItemActive: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  sessionTitle: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chatContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '16px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    overflow: 'hidden',
    minHeight: 0,
    position: 'relative',
  },
  webchat: {
    height: '100%',
    width: '100%',
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '8px',
      marginBottom: '8px',
    },
    '& table th, & table td': {
      border: '1px solid #d1d5db',
      padding: '8px 12px',
      textAlign: 'left',
      whiteSpace: 'nowrap',
    },
    '& table th': {
      backgroundColor: '#f3f4f6',
      fontWeight: 600,
    },
    '& table tr:nth-child(even)': {
      backgroundColor: '#f9fafb',
    },
  },
  centered: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
    textAlign: 'center',
  },
  transcript: {
    position: 'absolute',
    inset: 0,
    overflowY: 'auto',
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  transcriptHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalS,
  },
  transcriptMessage: {
    maxWidth: '80%',
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
  },
  transcriptMessageUser: {
    alignSelf: 'flex-end',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  transcriptMessageBot: {
    alignSelf: 'flex-start',
    backgroundColor: tokens.colorNeutralBackground3,
  },
});

interface DirectLineTokenResponse {
  token: string;
  conversationId?: string;
}

interface ChatMessage {
  role: string;
  content: string;
  timestamp?: string;
}

interface SessionSummary {
  id: string;
  title: string | null;
  updatedAt: string;
}

interface SessionDetail extends SessionSummary {
  messages: ChatMessage[];
}

type ConnectionStatus = 'loading' | 'ready' | 'error';

/**
 * Renders the official BotFramework WebChat UI, wired to the Copilot Studio
 * bot via a Direct Line token, plus a history sidebar backed by
 * `/api/assistant/sessions`. The live conversation is entirely handled by
 * Direct Line/WebChat; this component only listens on `directLine.activity$`
 * to capture a transcript and persist it for later browsing — clicking a
 * past session shows a read-only transcript, it does not resume the live
 * bot conversation (Direct Line manages that on its own).
 * The `botframework-webchat` script is loaded from CDN in index.html (NOT an
 * npm dependency — the package is large enough to cause OOM issues during
 * install/build in this environment).
 * Reference: https://github.com/microsoft/Agents/tree/main/samples/nodejs/copilotstudio-webchat-react
 */
export const AssistantPage = () => {
  const styles = useStyles();
  const webchatRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<ConnectionStatus>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [resetToken, setResetToken] = useState(0);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [viewingSession, setViewingSession] = useState<SessionDetail | null>(null);

  const currentSessionIdRef = useRef<string | null>(null);
  const messagesRef = useRef<ChatMessage[]>([]);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadSessions = async () => {
    try {
      const { results } = await apiClient.get<{ results: SessionSummary[] }>('/api/assistant/sessions');
      setSessions(results);
    } catch {
      // Non-fatal — history is a convenience feature, chat still works without it.
    }
  };

  useEffect(() => {
    void loadSessions();
  }, []);

  const persistTranscript = () => {
    if (messagesRef.current.length === 0) return;
    void apiClient
      .post<{ id: string; title: string | null; updatedAt: string }>('/api/assistant/sessions', {
        id: currentSessionIdRef.current ?? undefined,
        messages: messagesRef.current,
      })
      .then((saved) => {
        currentSessionIdRef.current = saved.id;
        setActiveSessionId(saved.id);
        void loadSessions();
      })
      .catch(() => {
        // Non-fatal — persistence failures should not disrupt the live chat.
      });
  };

  const scheduleSave = () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(persistTranscript, 1500);
  };

  useEffect(() => {
    let cancelled = false;
    let currentDirectLine: { end?: () => void } | null = null;
    let activitySubscription: { unsubscribe: () => void } | null = null;
    setStatus('loading');
    setViewingSession(null);
    currentSessionIdRef.current = null;
    messagesRef.current = [];
    setActiveSessionId(null);

    const init = async () => {
      try {
        const { token } = await apiClient.post<DirectLineTokenResponse>(
          '/api/assistant/directline-token',
          {}
        );
        if (cancelled) return;

        if (!window.WebChat) {
          throw new Error('BotFramework WebChat script failed to load from CDN');
        }
        if (!webchatRef.current) return;
        webchatRef.current.innerHTML = '';

        const directLine = window.WebChat.createDirectLine({
          token,
          domain: 'https://europe.directline.botframework.com/v3/directline',
        });
        currentDirectLine = directLine;

        activitySubscription = directLine.activity$.subscribe((activity) => {
          if (activity.type !== 'message' || !activity.text) return;
          const role = activity.from?.role === 'user' ? 'user' : 'bot';
          messagesRef.current = [
            ...messagesRef.current,
            { role, content: activity.text, timestamp: new Date().toISOString() },
          ];
          scheduleSave();
        });

        const webSpeechPonyfillFactory = await createSpeechPonyfillFactory();
        if (cancelled) return;

        const store = createManualSubmitWebChatStore();

        const renderOpts = (withSpeech: boolean) => ({
          directLine,
          ...(store ? { store } : {}),
          ...(withSpeech && webSpeechPonyfillFactory ? { webSpeechPonyfillFactory } : {}),
          locale: 'fr-FR',
          styleOptions: {
            hideUploadButton: true,
            rootHeight: '100%',
            rootWidth: '100%',
            bubbleBackground: tokens.colorNeutralBackground3,
            bubbleFromUserBackground: tokens.colorBrandBackground,
            bubbleFromUserTextColor: tokens.colorNeutralForegroundOnBrand,
            bubbleMaxWidth: 900,
            bubbleMinWidth: 400,
          },
        });
        try {
          window.WebChat.renderWebChat(renderOpts(true), webchatRef.current);
        } catch {
          window.WebChat.renderWebChat(renderOpts(false), webchatRef.current);
        }
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(err instanceof Error ? err.message : 'Unknown error');
        setStatus('error');
      }
    };

    void init();
    return () => {
      cancelled = true;
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      // Fully tear down the previous conversation so a "new chat" click gets
      // a clean DirectLine + WebChat pair instead of trying to reuse the
      // already-connected one, which throws
      // "DirectLineJS: It is connected, we cannot set user id" and blanks
      // the chat.
      try { activitySubscription?.unsubscribe(); } catch { /* noop */ }
      try { currentDirectLine?.end?.(); } catch { /* noop */ }
      if (webchatRef.current) webchatRef.current.innerHTML = '';
    };
  }, [resetToken]);

  const handleNewChat = () => {
    persistTranscript();
    setResetToken((n) => n + 1);
  };

  const handleOpenSession = async (id: string) => {
    try {
      const session = await apiClient.get<SessionDetail>(`/api/assistant/sessions/${id}`);
      setViewingSession(session);
      setActiveSessionId(id);
    } catch {
      // Ignore — session may have been deleted concurrently.
    }
  };

  const handleDeleteSession = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await apiClient.delete(`/api/assistant/sessions/${id}`);
      if (viewingSession?.id === id) setViewingSession(null);
      void loadSessions();
    } catch {
      // Ignore — best-effort cleanup.
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div>
          <Text as="h1" size={600} weight="semibold">AI Assistant</Text>
          <br />
          <Text className={styles.subtitle} size={300}>
            Generate quotes in natural language — powered by Copilot Studio
          </Text>
        </div>
      </div>

      <div className={styles.body}>
        {!sidebarCollapsed && (
          <div className={styles.sidebar}>
            <div className={styles.sidebarHeader}>
              <Text weight="semibold" size={300}>History</Text>
              <Tooltip content="Collapse history" relationship="label">
                <Button appearance="subtle" icon={<PanelLeftContract20Regular />} onClick={() => setSidebarCollapsed(true)} />
              </Tooltip>
            </div>
            <Button appearance="secondary" icon={<Add20Regular />} onClick={handleNewChat}>
              New chat
            </Button>
            {sessions.length === 0 && (
              <Text size={200} className={styles.subtitle}>No previous conversations yet.</Text>
            )}
            {sessions.map((s) => (
              <div
                key={s.id}
                className={`${styles.sessionItem} ${activeSessionId === s.id ? styles.sessionItemActive : ''}`}
                onClick={() => void handleOpenSession(s.id)}
              >
                <Text size={200} className={styles.sessionTitle}>{s.title || 'New chat'}</Text>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Delete20Regular />}
                  onClick={(e) => void handleDeleteSession(s.id, e)}
                />
              </div>
            ))}
          </div>
        )}
        {sidebarCollapsed && (
          <Tooltip content="Show history" relationship="label">
            <Button appearance="subtle" icon={<PanelLeftExpand20Regular />} onClick={() => setSidebarCollapsed(false)} />
          </Tooltip>
        )}

        <div className={styles.chatContainer}>
          {status === 'loading' && (
            <div className={styles.centered}>
              <Spinner label="Connecting to the assistant..." />
            </div>
          )}
          {status === 'error' && (
            <div className={styles.centered}>
              <Text weight="semibold">Unable to connect to the assistant</Text>
              <Text size={300}>{errorMessage}</Text>
            </div>
          )}
          <div key={resetToken} ref={webchatRef} className={styles.webchat} style={{ display: status === 'ready' && !viewingSession ? 'block' : 'none' }} />

          {viewingSession && (
            <div className={styles.transcript}>
              <div className={styles.transcriptHeader}>
                <Text weight="semibold">{viewingSession.title || 'Conversation'}</Text>
                <Button appearance="secondary" onClick={() => setViewingSession(null)}>Back to chat</Button>
              </div>
              {viewingSession.messages.map((m, i) => (
                <div
                  key={i}
                  className={`${styles.transcriptMessage} ${m.role === 'user' ? styles.transcriptMessageUser : styles.transcriptMessageBot}`}
                >
                  <Text>{m.content}</Text>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
